import { defineComponent as _defineComponent } from 'vue'
import { computed, onBeforeMount, onBeforeUnmount, ref } from 'vue'
import {
  emitRtDialogClosedWithoutSubmit,
  emitter,
  FORCE_HIDE_RT_DIALOGS
} from '@/plugins/emitter'
import RmSurveyCompleted from '@/components/realtime-transfer/RmSurveyCompleted.vue'
import RmSurveyOptionList from '@/components/realtime-transfer/RmSurveyOptionList.vue'
import { useAuth } from '@/composables/useAuth'
import { useSite } from '@/composables/useSite'
import { postRmAnswer, postRmDisplayLog } from '@/services/rm-survey-client'
import {
  RM_FREE_TEXT_MAX_LENGTH,
  rmSurveySubmitSchema
} from '@/validation/rmSurveyAnswer'
import {
  RM_SURVEY_DIALOG_TYPE,
  buildAnswers,
  needsFreeText,
  parseRmSurvey,
  resolveDeviceFlag,
  surveyStep,
  toggleSelection,
  type RmSelections,
  type RmSurvey
} from '@/utils/realtime-transfer/rmSurvey'


export default /*#__PURE__*/_defineComponent({
  __name: 'RmSurveyDialog',
  setup(__props) {

const SEND_ERROR = '送信できませんでした。時間をおいてお試しください'

const { user } = useAuth()
const { appFlag } = useSite()

const dialogVisible = ref(false)
const survey = ref<RmSurvey | null>(null)
const stepIndex = ref(0)
const selections = ref<RmSelections>({})
const freeTexts = ref<Record<string, string>>({})
const submitting = ref(false)
const submitted = ref(false)
const errorMessage = ref('')
const showDownloadCompleted = ref(true)
const formatId = ref('')
const rtConditionId = ref<number | null>(null)
const displayHistoryId = ref<number | null>(null)
const deviceFlag = resolveDeviceFlag(
  typeof navigator === 'undefined' ? '' : navigator.userAgent
)

const content = computed(() => survey.value?.dialogContent)
const step = computed(() =>
  survey.value
    ? surveyStep(survey.value, selections.value, stepIndex.value)
    : null
)
const currentSelected = computed(() =>
  step.value ? selections.value[step.value.question.questionCode] || [] : []
)

function onSelect(code: string) {
  if (!step.value) return
  const question = step.value.question
  selections.value = {
    ...selections.value,
    [question.questionCode]: toggleSelection(
      question,
      currentSelected.value,
      code
    )
  }
  errorMessage.value = ''
}

function onNext() {
  if (!step.value || currentSelected.value.length === 0) return
  if (step.value.isLast) {
    submit()
    return
  }
  stepIndex.value += 1
}

function onOpen(argList: unknown) {
  const args = Array.isArray(argList) ? argList : []
  const dialogInfo = (args[3] || {}) as Record<string, unknown>
  const parsed = parseRmSurvey(dialogInfo.rmSurvey)
  if (!parsed) {
    // 中身が壊れている RM 行は出さずに次の RT へ進める
    emitRtDialogClosedWithoutSubmit()
    return
  }
  survey.value = parsed
  stepIndex.value = 0
  selections.value = {}
  // Vue 2.7 は後から足したキーを検知しないので、全設問のキーを先に作る
  freeTexts.value = Object.fromEntries(
    parsed.questions.map((q) => [q.questionCode, ''])
  )
  submitted.value = false
  submitting.value = false
  errorMessage.value = ''
  showDownloadCompleted.value = dialogInfo.showDownloadCompletedTitle !== false
  formatId.value = typeof args[0] === 'string' ? args[0] : ''
  rtConditionId.value =
    typeof dialogInfo.conditionId === 'number' ? dialogInfo.conditionId : null
  displayHistoryId.value = null
  dialogVisible.value = true
  logDisplay(parsed.rmConditionId)
}

function logDisplay(rmConditionId: number) {
  const uuid = user.value?.uid
  if (!uuid) return
  postRmDisplayLog({
    uuid,
    rm_condition_id: rmConditionId,
    rt_condition_id: rtConditionId.value,
    format_id: formatId.value,
    device_flag: deviceFlag,
    app_flag: appFlag.value,
    site_flag: 'r'
  })
    .then((id) => {
      displayHistoryId.value = id
    })
    .catch((err) => console.error(err))
}

async function submit() {
  if (!survey.value || submitting.value) return
  const parsed = rmSurveySubmitSchema.safeParse({
    uuid: user.value?.uid ?? '',
    rm_condition_id: survey.value.rmConditionId,
    rm_display_history_id: displayHistoryId.value,
    format_id: formatId.value,
    device_flag: deviceFlag,
    app_flag: appFlag.value,
    site_flag: 'r',
    answers: buildAnswers(survey.value, selections.value, freeTexts.value)
  })
  if (!parsed.success) {
    errorMessage.value =
      parsed.error.issues[0]?.message || '回答を確認してください'
    return
  }
  submitting.value = true
  try {
    if (await postRmAnswer(parsed.data)) {
      submitted.value = true
    } else {
      errorMessage.value = SEND_ERROR
    }
  } catch (err) {
    console.error(err)
    errorMessage.value = SEND_ERROR
  } finally {
    submitting.value = false
  }
}

function onVisibleInput(next: unknown) {
  dialogVisible.value = next === true
}

function closeDialog() {
  dialogVisible.value = false
}

function onModalClosed() {
  // 回答したかどうかに関係なく、ワークゲート等の次の RT へ進める（Workgate / ヤギバディと同じ）
  emitRtDialogClosedWithoutSubmit()
}

onBeforeMount(() => {
  emitter.on(RM_SURVEY_DIALOG_TYPE, onOpen)
  emitter.on(FORCE_HIDE_RT_DIALOGS, closeDialog)
})
onBeforeUnmount(() => {
  emitter.off(RM_SURVEY_DIALOG_TYPE, onOpen)
  emitter.off(FORCE_HIDE_RT_DIALOGS, closeDialog)
})

return { __sfc: true,SEND_ERROR, user, appFlag, dialogVisible, survey, stepIndex, selections, freeTexts, submitting, submitted, errorMessage, showDownloadCompleted, formatId, rtConditionId, displayHistoryId, deviceFlag, content, step, currentSelected, onSelect, onNext, onOpen, logDisplay, submit, onVisibleInput, closeDialog, onModalClosed, RmSurveyCompleted, RmSurveyOptionList, RM_FREE_TEXT_MAX_LENGTH, needsFreeText }
}

})